import moment from 'moment';
/* eslint-disable no-param-reassign */
import { createAsyncThunk, createAction, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  RecorderSettings,
  RecoverySettings,
  TeamRole,
  UISettings,
} from '../../types/state';
import { requestStatusType, IntegrationsSettings } from '../../types';
import {
  getSettings,
  newUpdateSettings,
  updateProfile,
  addUserTag,
  V2SettingsKeys,
} from '../../services/authService';
import {
  BackupRecord,
  deleteBackupRecord,
  deleteChunks,
  getBackupRecords,
  listChunks,
  loadChunk,
  updateBackupRecordStatus,
} from '../../lib/recovery';
import {
  triggerExtensionRecoveryReupload,
  isExtensionInstalled,
} from '../../lib/extensionRecovery';
import { RootState } from '../../reducers';
import {
  createPartUploadUrl,
  initializeMultipartUpload,
  uploadChunkToS3,
  finalizeUpload,
} from '../../recorder/uploadService';
import { getInitialState } from '../app/appSlice';
import {
  addUserWebhookUrl,
  removeUserWebhookUrl,
  disconnectZoomIntegration,
} from '../../services/integrationService';
import { addNotification } from '../dashboard/dashboardSlice';
import { PersonalAccessToken } from '../../types/PersonalAccessToken';
import {
  createToken,
  listPermissions,
  rotateToken,
  revokeToken,
  listTokens,
} from './API/thunk';
import { reconstructChunks } from '../../utils/chunkUtils';

export const SETTINGS = [
  'user',
  'team',
  'recorder',
  'plugin',
  'billing',
  'ai',
  'recovery',
  'usage',
  'ui',
  'integrations',
  'meetingbot',
  'display',
  'features',
  'tags',
] as const;

export type SettingsKeys = (typeof SETTINGS)[number];

export interface UserSettings {
  _id: string;
  name: string;
  email: string;
  picture: string;
  role: TeamRole;
  deleteRequestedAt?: string;
  createdAt: string;
  tags: {
    [key: string]: string;
  };
  integrations: IntegrationsSettings;
  locale?: string;
  customButtons?: string[];
}

interface UserSettingsResponse {
  name: string;
  email: string;
  picture: string;
  locale?: string;
  integrations?: IntegrationsSettings;
}

// interface UserSettingsResponse {
//   name: string;
//   email: string;
//   picture: string;
// }

interface APIState {
  permissions: {
    available: string[];
    status: requestStatusType;
    error: string;
  };
  tokens: {
    visible: {
      [_id: string]: {
        operation: 'create' | 'rotate' | 'revoke';
        token?: string;
        tokenInfo?: PersonalAccessToken;
        status: requestStatusType;
        error: string;
      };
    };
    active: {
      items: PersonalAccessToken[];
      status: requestStatusType;
      error: string;
    };
  };
}

interface SettingsState {
  status: requestStatusType;
  updateSettings: {
    status: requestStatusType;
  };
  user: UserSettings;
  recorder: RecorderSettings;
  plugin: RecorderSettings;
  recovery: RecoverySettings;
  ui: UISettings;
  api: APIState;
}

interface FetchSettingsResponse {
  key: SettingsKeys;
  data: RecorderSettings | UserSettingsResponse | UISettings;
}

export type NewSettings = RecorderSettings | UserSettings | UISettings;

const initialSettings: RecorderSettings = {
  audioSettings: {
    browserAudio: false,
    microphoneAudio: false,
    systemAudio: false,
  },
  videoSettings: {
    webCam: false,
    screen: true,
  },
  deviceSettings: {
    webcamDeviceId: '',
    microphoneDeviceId: '',
  },
  recordingControlSettings: {
    startRecordingAuto: false,
    autoDownload: true,
    defaultRecordingFormat: 'mkv',
    maxRecordingTimeMins: 10,
  },
  uiSettings: {
    hideLogo: false,
    recorderButtonText: 'Share Your Screen',
    position: 'BOTTOM_LEFT',
    helpText: 'Feedbacks',
    identificationForm: true,
  },
  otherSettings: {
    webhookUrl: '',
    hideTitleAndDescriptionDialog: true,
    defaultTranscriptionLanguage: undefined,
    formatProfile: 'mkv',
    generatePublicUrl: false,
    downloadSharedVideos: false,
    emailAlerts: false,
  },
};

const initialState: SettingsState = {
  status: 'idle',
  user: {
    _id: '',
    name: '',
    email: '',
    picture: '',
    role: 'Guest',
    createdAt: '',
    tags: {},
    integrations: {
      google: {
        calendar: {
          isActive: false,
          reconnect: false,
          personName: null,
          personEmail: null,
        },
        googleMeet: {
          fileId: null,
        },
      },
      webhooks: [],
      microsoft: {
        calendar: {
          isActive: false,
          reconnect: false,
          personName: null,
          personEmail: null,
        },
      },
    },
    locale: 'en-US', // Default locale set to English (US)
  },
  recorder: initialSettings,
  plugin: initialSettings,
  ui: {
    folderListExpanded: false,
    filesOnSidebar: false,
  },
  recovery: {
    backupRecords: [],
    recoveryState: {},
    status: 'idle',
    alertClosed: true,
  },
  updateSettings: {
    status: 'idle',
  },
  api: {
    permissions: {
      available: [],
      status: 'idle',
      error: '',
    },
    tokens: {
      visible: {},
      active: {
        items: [],
        status: 'idle',
        error: '',
      },
    },
  },
};

export const updateUserProfile = createAsyncThunk(
  'settings/updateUserProfile',
  async (
    data: Parameters<typeof updateProfile>[0],
    { rejectWithValue, getState },
  ) => {
    try {
      const response = await updateProfile(data);
      if (response?.status === 200) {
        const state = getState() as RootState;
        const currentUser = state.settings.user;

        // Don't show success message for auto-updates
        // The UI provides immediate feedback through the update itself

        // Handle profile picture update
        if (data.picture === '') {
          // For profile picture removal, use empty string
          return {
            ...response.data.data,
            picture: '', // Empty string to remove the picture on the server
            name: data.name || currentUser.name,
          };
        }

        if (data.picture && data.pictureMime) {
          // For profile picture update with new image
          return {
            ...response.data.data,
            picture: `data:${data.pictureMime};base64,${data.picture}`,
            name: data.name || currentUser.name,
          };
        }

        // For name or integrations update
        const result = {
          ...response.data.data,
          name: data.name || currentUser.name,
          integrations: {
            ...currentUser.integrations,
            ...(response.data.data.integrations || {}),
            ...(data.integrations || {}),
          },
        };
        return result;
      }
      throw new Error('Failed to update profile');
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to update profile' },
      );
    }
  },
);

export const loadBackupRecordsThunk = createAsyncThunk(
  'settings/recovery/loadBackupRecords',
  async (_, { rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const teamId = state.team.selectedTeam?.teamId;
    try {
      const backupRecords = await getBackupRecords(teamId);
      backupRecords.sort((a, b) => b.startedAt - a.startedAt);

      // Create recovery state based on backup record status
      const recoveryState = Object.fromEntries(
        backupRecords.map((r) => {
          let recoveryStatus: RecoverySettings['recoveryState'][string]['status'] =
            'pending';
          // Default to 'pending' if status is missing (for backwards compatibility)
          const recordStatus = r.status || 'pending';
          // Only mark as 'success' if the file was actually recovered through the recovery process
          if (recordStatus === 'recovered') {
            recoveryStatus = 'success';
          }
          return [r.fileId, { status: recoveryStatus }];
        }),
      );
      return { backupRecords, recoveryState };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Do not use this directly, use useUserTags hook instead
export const addTagToUserThunk = createAsyncThunk(
  'settings/user/tag/add',
  async (
    { key, value }: { key: string; value: string },
    { rejectWithValue },
  ) => {
    try {
      await addUserTag({ key, value });
      return { key, value };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const uploadBackupRecordProgress = createAction<{
  total: number;
  loaded: number;
  oldFileId: string;
}>('settings/recovery/uploadBackupFile/progress');

export const uploadBackupRecordThunk = createAsyncThunk(
  'settings/recovery/uploadBackupFile',
  async (
    backupRecord: BackupRecord,
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const state = getState() as RootState;

      const teamId =
        backupRecord.teamId ||
        state.team.selectedTeam?.teamId ||
        state.team.teams[0].teamId;
      const folderId =
        backupRecord.folderId || state.library.selectedFolder?._id || '';

      const { fileId: oldFileId, uploadId: oldUploadId } = backupRecord;

      // Check if chunks exist in browser storage
      const chunks = await listChunks(oldFileId);

      // If no chunks in browser and extension is installed, try extension recovery
      if (chunks.length === 0 && isExtensionInstalled()) {
        try {
          const contentType = backupRecord.contentType || 'video/x-matroska';
          const result = await triggerExtensionRecoveryReupload({
            fileId: oldFileId,
            teamId,
            folderId,
            uploadId: oldUploadId || '',
            contentType,
          });

          // Extension recovery uses the same fileId/uploadId
          const recoveredFileId = result.file._id || oldFileId;

          // Add success notification
          const title = backupRecord.title || 'Recovered Recording';
          dispatch(
            addNotification({
              id: Date.now().toString(),
              type: 'transcription',
              title: 'Recording Recovered',
              message: `"${title}" has been recovered successfully from extension`,
              link: `/library/${teamId}/file/${recoveredFileId}`,
              read: false,
              timestamp: Date.now(),
            }),
          );

          // Clean up extension backup record
          try {
            const { deleteExtensionBackupRecord } = await import(
              '../../lib/extensionRecovery'
            );
            await deleteExtensionBackupRecord(oldFileId);
          } catch (cleanupError) {
            console.warn(
              'Failed to clean up extension backup record:',
              cleanupError,
            );
          }

          // Refresh the file list
          const { fetchFiles } = await import('../library/librarySlice');
          dispatch(
            fetchFiles({
              teamId,
              folderId,
              page: 1,
              limit: 20,
            }),
          );

          // Extension recovery successful - return same structure as browser recovery
          return {
            newFileId: recoveredFileId,
            newUploadId: oldUploadId || '',
            oldFileId,
            oldUploadId,
            status: 'success',
          };
        } catch (extensionError) {
          console.error('Extension recovery failed:', extensionError);
          throw new Error(
            `Extension recovery failed: ${extensionError.message}`,
          );
        }
      }

      // Browser storage recovery (existing logic)
      const response = await initializeMultipartUpload({
        teamId,
        folderId,
        contentType: 'video/x-matroska',
      });
      const { fileId: newFileId, uploadId: newUploadId } = response;
      dispatch(
        uploadBackupRecordProgress({
          oldFileId,
          total: chunks.length,
          loaded: 0,
        }),
      );

      if (chunks.length === 0) {
        console.error('Recovery failed: No chunks found', {
          fileId: oldFileId,
        });
        throw new Error('No chunks found for recovery');
      }

      // Load all backup chunks in sequential order, filtering out missing chunks
      const backupChunks: Blob[] = [];
      let missingChunkCount = 0;

      for (let i = 0; i < chunks.length; i += 1) {
        const partNumber = i + 1;
        /* eslint-disable no-await-in-loop */
        const chunk = await loadChunk(oldFileId, partNumber);
        /* eslint-enable no-await-in-loop */
        if (chunk) {
          backupChunks.push(chunk);
        } else {
          missingChunkCount += 1;
          console.warn(
            `Missing chunk with part number ${partNumber}, skipping...`,
          );
        }

        // Update progress as we process backup chunks
        dispatch(
          uploadBackupRecordProgress({
            oldFileId,
            total: chunks.length,
            loaded: i + 1,
          }),
        );
      }

      if (backupChunks.length === 0) {
        console.error('Recovery failed: All chunks are missing', {
          fileId: oldFileId,
          totalChunks: chunks.length,
          missingChunks: missingChunkCount,
        });
        throw new Error('All chunks are missing for recovery');
      }

      if (missingChunkCount > 0) {
        console.warn(
          `Recovery proceeding with ${backupChunks.length} chunks (${missingChunkCount} missing)`,
          {
            fileId: oldFileId,
            availableChunks: backupChunks.length,
            missingChunks: missingChunkCount,
          },
        );
      }

      // Use shared utility to reconstruct 5MB chunks from small backup chunks
      const reconstructedChunks = reconstructChunks(
        backupChunks,
        undefined,
        'video/x-matroska',
      );

      console.log(
        `Reconstructed ${reconstructedChunks.length} chunks from ${backupChunks.length} backup chunks (${missingChunkCount} missing)`,
      );

      // Now upload the reconstructed 5MB chunks
      for (let i = 0; i < reconstructedChunks.length; i += 1) {
        /* eslint-disable no-await-in-loop */
        const chunk = reconstructedChunks[i];
        const url = await createPartUploadUrl({
          teamId,
          folderId,
          fileId: newFileId,
          uploadId: newUploadId,
          partNumber: i + 1,
          contentType: 'video/x-matroska',
        });
        await uploadChunkToS3({ uploadUrl: url, chunk });
        /* eslint-enable no-await-in-loop */
      }

      const title = `Recovered ${moment(backupRecord.startedAt).format(
        'h:mm a MMM D YYYY',
      )}`;

      await finalizeUpload({
        teamId,
        folderId,
        uploadId: newUploadId,
        fileId: newFileId,
        contentType: 'video/x-matroska',
        notes: [],
        title,
      });

      // Add success notification
      const message =
        missingChunkCount > 0
          ? `"${title}" has been recovered successfully (${missingChunkCount} missing chunks were skipped)`
          : `"${title}" has been recovered successfully`;

      dispatch(
        addNotification({
          id: Date.now().toString(),
          type: 'transcription',
          title: 'Recording Recovered',
          message,
          link: `/library/${teamId}/file/${newFileId}`,
          read: false,
          timestamp: Date.now(),
        }),
      );

      // Clean up the backup record since recovery was successful
      // Use the same API calls that deleteBackupRecordThunk uses
      try {
        await deleteChunks(oldFileId);
        await deleteBackupRecord(oldFileId);
      } catch (error) {
        console.warn(
          'Failed to clean up backup record after successful recovery:',
          error,
        );
      }

      // Refresh the file list to show the recovered file immediately
      const { fetchFiles } = await import('../library/librarySlice');
      dispatch(
        fetchFiles({
          teamId,
          folderId,
          page: 1,
          limit: 20,
        }),
      );

      return {
        newFileId,
        newUploadId,
        oldFileId,
        oldUploadId,
        status: 'success',
      };
    } catch (error) {
      // Clean up the backup record since recovery failed
      try {
        await deleteChunks(backupRecord.fileId);
        await deleteBackupRecord(backupRecord.fileId);

        // Remove the failed backup record from state manually (same as deleteBackupRecordThunk.fulfilled)
        dispatch({
          type: 'settings/removeFailedBackupRecord',
          payload: backupRecord.fileId,
        });
      } catch (cleanupError) {
        console.warn(
          'Failed to clean up backup record after failed recovery:',
          cleanupError,
        );
      }

      // Hide the progress alert since recovery failed
      dispatch({ type: 'settings/setRecoveryAlertClosed', payload: true });

      // Show user-friendly dialog explaining the failure
      const { pushModal } = await import('../globalModals/globalModalsSlice');
      dispatch(
        pushModal({
          type: 'recovery-failure-modal',
        }),
      );

      return rejectWithValue({ error, backupRecord });
    }
  },
);

export const deleteBackupRecordThunk = createAsyncThunk(
  'settings/recovery/deleteBackupRecord',
  async (fileId: string, { rejectWithValue }) => {
    try {
      // Try to delete from browser storage
      await deleteChunks(fileId);
      await deleteBackupRecord(fileId);

      // Also try to delete from extension if installed
      if (isExtensionInstalled()) {
        try {
          const { deleteExtensionBackupRecord } = await import(
            '../../lib/extensionRecovery'
          );
          await deleteExtensionBackupRecord(fileId);
        } catch (extensionError) {
          console.warn('Failed to delete from extension:', extensionError);
          // Don't fail the whole operation if extension delete fails
        }
      }

      return fileId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const dismissBackupRecordThunk = createAsyncThunk(
  'settings/recovery/dismissBackupRecord',
  async (fileId: string, { rejectWithValue }) => {
    try {
      // Update status in browser storage
      await updateBackupRecordStatus(fileId, 'dismissed');

      // Also update in extension if installed
      if (isExtensionInstalled()) {
        try {
          const { deleteExtensionBackupRecord } = await import(
            '../../lib/extensionRecovery'
          );
          // For dismiss, we delete from extension (since extension doesn't have dismiss state)
          await deleteExtensionBackupRecord(fileId);
        } catch (extensionError) {
          console.warn('Failed to dismiss in extension:', extensionError);
          // Don't fail the whole operation if extension dismiss fails
        }
      }

      return fileId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const fetchSettings = createAsyncThunk<
  FetchSettingsResponse,
  V2SettingsKeys
>(
  'settings/fetchSettings',
  async (key: V2SettingsKeys, { rejectWithValue }) => {
    try {
      const settings = await getSettings(key);
      return { key, data: settings };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async (
    { key, newSettings }: { key: SettingsKeys; newSettings: NewSettings },
    { rejectWithValue },
  ) => {
    try {
      const response = await newUpdateSettings(key, newSettings);
      if (response?.status === 200) {
        return { key, data: response.data.data };
      }
      throw new Error('Failed to update settings');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const addWebhookUrlThunk = createAsyncThunk(
  'settings/integrations/webhook/add',
  async (
    {
      name,
      url,
      templateId,
    }: { name: string; url: string; templateId: string },
    { rejectWithValue },
  ) => {
    try {
      await addUserWebhookUrl({ name, url, templateId });
      return { name, url, templateId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const removeWebhookUrlThunk = createAsyncThunk(
  'settings/integrations/webhook/remove',
  async ({ url }: { url: string }, { rejectWithValue }) => {
    try {
      await removeUserWebhookUrl({ url });
      return { url };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const disconnectZoom = createAsyncThunk(
  'settings/disconnectZoom',
  async (_, { dispatch }) => {
    try {
      const message = await disconnectZoomIntegration();
      dispatch(getInitialState());
      dispatch(
        addNotification({
          id: Date.now().toString(),
          title: 'Zoom Integration',
          message: message || 'Zoom account disconnected successfully',
          type: 'team',
          read: false,
          timestamp: Date.now(),
        }),
      );
      return true;
    } catch (error) {
      dispatch(
        addNotification({
          id: Date.now().toString(),
          title: 'Zoom Integration',
          message: 'Failed to disconnect Zoom account',
          type: 'error',
          read: false,
          timestamp: Date.now(),
        }),
      );
      throw error;
    }
  },
);

export const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    ...initialState,
    recovery: {
      ...initialState.recovery,
      isRecoveryAlertMinimized: false,
    },
  },
  reducers: {
    setUser: (state, action: PayloadAction<Partial<UserSettings>>) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    makeUpdateSettingsStatusIdle: (state) => {
      state.updateSettings.status = 'idle';
    },
    clearVisibleToken: (state, action: PayloadAction<string>) => {
      delete state.api.tokens.visible[action.payload];
    },
    setRecoveryAlertClosed: (state, action: PayloadAction<boolean>) => {
      state.recovery.alertClosed = action.payload;
    },
    setRecoveryAlertMinimized: (state, action: PayloadAction<boolean>) => {
      state.recovery.isRecoveryAlertMinimized = action.payload;
    },
    removeFailedBackupRecord: (state, action: PayloadAction<string>) => {
      const fileIdToRemove = action.payload;
      // Remove from backup records
      state.recovery.backupRecords = state.recovery.backupRecords.filter(
        (r) => r.fileId !== fileIdToRemove,
      );
      // Remove from recovery state
      delete state.recovery.recoveryState[fileIdToRemove];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getInitialState.fulfilled, (state, action) => {
        // Settings from /init endpoint
        state.ui = action.payload.settings.ui;
        state.recorder = action.payload.settings.recorder;
        state.plugin = action.payload.settings.plugin;
      })
      .addCase(fetchSettings.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        if (key === 'recorder' || key === 'plugin') {
          state[key] = data as RecorderSettings;
        } else if (key === 'user') {
          // Only update specific user fields from settings endpoint
          const userSettings = data as UserSettingsResponse;
          state.user = {
            ...state.user,
            name: userSettings.name,
            email: userSettings.email,
            picture: userSettings.picture,
            integrations: {
              ...state.user.integrations,
              ...userSettings.integrations,
            },
          };
        } else if (key === 'ui') {
          state.ui = data as UISettings;
        }
        state.status = 'succeeded';
      })
      .addCase(fetchSettings.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(updateSettings.pending, (state, action) => {
        const { key, newSettings } = action.meta.arg;
        state.updateSettings.status = 'pending';
        if (key === 'recorder' || key === 'plugin') {
          state[key] = newSettings as RecorderSettings;
        } else if (key === 'ui' || key === 'meetingbot') {
          state.ui = newSettings as UISettings;
        } else if (key === 'user') {
          // Only update specific user fields
          const userSettings = newSettings as UserSettingsResponse;
          state.user = {
            ...state.user,
            name: userSettings.name,
            email: userSettings.email,
            picture: userSettings.picture,
            integrations: {
              ...state.user.integrations,
              ...userSettings.integrations,
            },
          };
        }
      })
      .addCase(updateSettings.fulfilled, (state) => {
        state.updateSettings.status = 'succeeded';
      })
      .addCase(updateSettings.rejected, (state) => {
        state.updateSettings.status = 'failed';
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.updateSettings.status = 'pending';
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.updateSettings.status = 'succeeded';
        if (action.payload) {
          // Update the user profile with the response data
          state.user = {
            ...state.user,
            ...action.payload,
          };

          // Ensure the picture field is properly updated
          if (
            action.payload &&
            typeof action.payload === 'object' &&
            'picture' in action.payload
          ) {
            state.user.picture = action.payload.picture as string;
          }

          // Ensure the name field is properly updated
          if (
            action.payload &&
            typeof action.payload === 'object' &&
            'name' in action.payload
          ) {
            state.user.name = action.payload.name as string;
          }

          // Ensure the integrations field is properly updated
          if (
            action.payload &&
            typeof action.payload === 'object' &&
            'integrations' in action.payload
          ) {
            state.user.integrations = {
              ...state.user.integrations,
              ...action.payload.integrations,
            };
          }
        }
      })
      .addCase(updateUserProfile.rejected, (state) => {
        state.updateSettings.status = 'failed';
      })
      .addCase(loadBackupRecordsThunk.fulfilled, (state, action) => {
        state.recovery.backupRecords = action.payload.backupRecords;
        state.recovery.recoveryState = action.payload.recoveryState;
        state.recovery.status = 'succeeded';
      })
      .addCase(loadBackupRecordsThunk.rejected, (state) => {
        state.recovery.status = 'failed';
      })
      .addCase(deleteBackupRecordThunk.fulfilled, (state, action) => {
        state.recovery.backupRecords = state.recovery.backupRecords.filter(
          (r) => r.fileId !== action.payload,
        );
        delete state.recovery.recoveryState[action.payload];
      })
      .addCase(deleteBackupRecordThunk.rejected, () => {
        // do nothing
      })
      .addCase(dismissBackupRecordThunk.fulfilled, (state, action) => {
        const record = state.recovery.backupRecords.find(
          (r) => r.fileId === action.payload,
        );
        if (record) {
          record.status = 'dismissed';
        }
      })
      .addCase(uploadBackupRecordThunk.pending, (state, action) => {
        const record = state.recovery.backupRecords.find(
          (r) => r.fileId === action.meta.arg.fileId,
        );
        if (record) {
          state.recovery.recoveryState[record.fileId] = {
            status: 'ongoing',
            progress: 0,
          };
          state.recovery.alertClosed = false;
          state.recovery.isRecoveryAlertMinimized = false;
        }
      })
      .addCase(uploadBackupRecordThunk.fulfilled, (state, action) => {
        const record = state.recovery.backupRecords.find(
          (r) => r.fileId === action.meta.arg.fileId,
        );
        if (record) {
          // Update the backup record status
          record.status = 'recovered';
          state.recovery.recoveryState[record.fileId] = {
            status: 'success',
            progress: 100,
          };
        }
      })
      .addCase(uploadBackupRecordThunk.rejected, (state, action) => {
        const record = state.recovery.backupRecords.find(
          (r) => r.fileId === action.meta.arg.fileId,
        );
        if (record) {
          state.recovery.recoveryState[record.fileId] = {
            status: 'error',
            error: action.error.message,
          };
        }
      })
      .addCase(uploadBackupRecordProgress, (state, action) => {
        const { oldFileId, total, loaded } = action.payload;
        if (!state.recovery.recoveryState[oldFileId]) {
          state.recovery.recoveryState[oldFileId] = {
            status: 'ongoing',
            progress: 0,
          };
          state.recovery.alertClosed = false;
          state.recovery.isRecoveryAlertMinimized = false;
        }

        const overallProgress = (loaded / total) * 100;
        state.recovery.recoveryState[oldFileId].progress = overallProgress;
      })
      .addCase(addTagToUserThunk.pending, (state, action) => {
        state.updateSettings.status = 'pending';
        state.user.tags[action.meta.arg.key] = action.meta.arg.value;
      })
      .addCase(addWebhookUrlThunk.fulfilled, (state, action) => {
        const { name, url } = action.payload;
        state.user.integrations.webhooks.push({ name, url });
      })
      .addCase(removeWebhookUrlThunk.fulfilled, (state, action) => {
        const { url } = action.payload;
        state.user.integrations.webhooks =
          state.user.integrations.webhooks.filter(
            (webhook) => webhook.url !== url,
          );
      })
      .addCase(disconnectZoom.fulfilled, (state) => {
        state.user.integrations.zoom = {
          zoomUserId: '',
          email: '',
          isConnected: false,
        };
      })
      .addCase(listPermissions.fulfilled, (state, action) => {
        state.api.permissions.available = action.payload.permissions;
      })
      .addCase(listPermissions.rejected, (state) => {
        state.api.permissions.status = 'failed';
        state.api.permissions.error = 'Failed to list permissions';
      })
      .addCase(createToken.fulfilled, (state, action) => {
        state.api.tokens.visible[action.payload.tokenInfo._id] = {
          operation: 'create',
          token: action.payload.token,
          tokenInfo: action.payload.tokenInfo,
          status: 'succeeded',
          error: '',
        };
        // Add the token to the active tokens
        state.api.tokens.active.items.push(action.payload.tokenInfo);
      })
      .addCase(createToken.rejected, (state, action) => {
        state.api.tokens.visible.new_token.operation = 'create';
        state.api.tokens.visible.new_token.status = 'failed';
        state.api.tokens.visible.new_token.token = undefined;
        state.api.tokens.visible.new_token.tokenInfo = undefined;
        state.api.tokens.visible.new_token.error =
          action.error.message || 'Failed to create token';
      })
      .addCase(rotateToken.fulfilled, (state, action) => {
        state.api.tokens.visible[action.payload.tokenInfo._id] = {
          operation: 'rotate',
          token: action.payload.token,
          tokenInfo: action.payload.tokenInfo,
          status: 'succeeded',
          error: '',
        };
        // Edit the token in the active tokens
        state.api.tokens.active.items = state.api.tokens.active.items.map(
          (token) =>
            token._id === action.payload.tokenInfo._id
              ? action.payload.tokenInfo
              : token,
        );
      })
      .addCase(rotateToken.rejected, (state, action) => {
        state.api.tokens.visible[action.meta.arg].status = 'failed';
        state.api.tokens.visible[action.meta.arg].token = undefined;
        state.api.tokens.visible[action.meta.arg].tokenInfo = undefined;
        state.api.tokens.visible[action.meta.arg].operation = 'rotate';
        state.api.tokens.visible[action.meta.arg].error =
          action.error.message || 'Failed to rotate token';
      })
      .addCase(revokeToken.fulfilled, (state, action) => {
        state.api.tokens.visible[action.meta.arg].status = 'succeeded';
        state.api.tokens.visible[action.meta.arg].token = undefined;
        state.api.tokens.visible[action.meta.arg].tokenInfo = undefined;
        state.api.tokens.visible[action.meta.arg].operation = 'revoke';
        state.api.tokens.visible[action.meta.arg].error = '';
        // Remove the token from the active tokens
        state.api.tokens.active.items = state.api.tokens.active.items.filter(
          (token) => token._id !== action.meta.arg,
        );
      })
      .addCase(revokeToken.rejected, (state, action) => {
        state.api.tokens.visible[action.meta.arg].status = 'failed';
        state.api.tokens.visible[action.meta.arg].token = undefined;
        state.api.tokens.visible[action.meta.arg].tokenInfo = undefined;
        state.api.tokens.visible[action.meta.arg].operation = 'revoke';
        state.api.tokens.visible[action.meta.arg].error =
          action.error.message || 'Failed to revoke token';
      })
      .addCase(listTokens.fulfilled, (state, action) => {
        state.api.tokens.active.status = 'succeeded';
        state.api.tokens.active.items = action.payload;
        state.api.tokens.active.error = '';
      })
      .addCase(listTokens.rejected, (state) => {
        state.api.tokens.active.status = 'failed';
        state.api.tokens.active.items = [];
        state.api.tokens.active.error = 'Failed to list tokens';
      });
  },
});

export const {
  setUser,
  makeUpdateSettingsStatusIdle,
  clearVisibleToken,
  setRecoveryAlertClosed,
  setRecoveryAlertMinimized,
  removeFailedBackupRecord,
} = settingsSlice.actions;
export default settingsSlice.reducer;
